/** @format */

import Alert from '@mui/material/Alert';
import {Box, LinearProgress, Typography} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {makeStyles, createStyles} from '@mui/styles';
import {add, format, parseISO} from 'date-fns';
import {ru} from 'date-fns/locale';

import BackHeader from 'components/BackHeader';
import ConnectedProviders from 'components/icons/ConnectedProviders';

import {formatRUB, safeDivide} from 'helpers/numberHelpers';

import NoSubscription from './NoSubscription';
import {useGetCurrentUserQuery} from './graphql/__generated__';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    value: {
      color: theme.palette.text.primary,
      fontSize: 16,
      fontWeight: 700,
      lineHeight: '20px',
    },
    totalPrice: {
      color: theme.palette.text.primary,
      fontSize: 20,
      fontWeight: 700,
      lineHeight: '22px',
    },
    companiesContainer: {
      display: 'flex',
      marginTop: theme.spacing(14),
      overflowX: 'scroll',
    },
    companyName: {
      color: theme.palette.text.primary,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: '16px',
      whiteSpace: 'nowrap',
    },
  }),
);

const Subscription = () => {
  const classes = useStyles();

  const {loading, error, data} = useGetCurrentUserQuery();

  if (
    loading ||
    !data?.currentUser ||
    typeof data.currentUser.currentAccountsPriceInfo.price !== 'number'
  ) {
    return <LinearProgress style={{flex: 1}} />;
  }
  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  const {tokensCount, companies, subscription} = data.currentUser;
  const {price, count} = data.currentUser.currentAccountsPriceInfo;

  if (!subscription.activeSubscription) {
    return <NoSubscription />;
  }

  const renderRow = (title: string, value: string | number | null) => {
    return (
      <Box display='flex' flexGrow={1} justifyContent='space-between' mt={2}>
        <Typography variant='body1'>{title}</Typography>
        <Typography className={classes.value}>{value}</Typography>
      </Box>
    );
  };

  const endDate = subscription.endDate ? parseISO(subscription.endDate) : null;
  const nextPaymentDate = endDate ? add(endDate, {days: 1}) : null;
  const formattedNextPaymentDate = nextPaymentDate
    ? format(nextPaymentDate, 'd MMMM', {
        locale: ru,
      })
    : '';
  const checkCost = typeof count === 'number' ? safeDivide(price, count) : 0;

  return (
    <>
      <Box mb={10}>
        <BackHeader />
      </Box>
      <Typography variant='h1'>{'Информация о подписке'}</Typography>

      <Box mt={6}>
        {renderRow('Дата следующего списания', formattedNextPaymentDate)}
        {renderRow('Количество оплаченных кабинетов', subscription.accounts ?? '')}
        {renderRow('Количество подключенных кабинетов', tokensCount)}
      </Box>

      <Box mt={6}>{renderRow('Стоимость проверки кабинета за месяц', formatRUB(checkCost))}</Box>

      <Box display='flex' flexGrow={1} justifyContent='space-between' mt={3}>
        <Typography className={classes.value}>Итого:</Typography>
        <Typography className={classes.totalPrice}>{formatRUB(price)}</Typography>
      </Box>

      <Box className={classes.companiesContainer}>
        {companies
          .filter(v => v.tokens.length > 0)
          .map((v, i) => (
            <Box key={i} display='flex' flexDirection='column' ml={i === 0 ? 0 : 8}>
              <Typography className={classes.companyName}>{v.name}</Typography>
              <Box mt={2}>
                <ConnectedProviders adTypes={['CONTEXT', 'TARGET']} size={40} tokens={v.tokens} />
              </Box>
            </Box>
          ))}
      </Box>
    </>
  );
};

export default Subscription;
